import React,{useState} from 'react';
import {Link} from 'react-router-dom';
import {motion,AnimatePresence} from 'framer-motion';
import {useAuth} from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiUser,FiMail,FiLock,FiSave,FiPlus,FiX,FiCheck,FiUserPlus,FiEdit,FiArchive,FiRefreshCw,FiEye,FiEyeOff}=FiIcons;

const ManageCoaches=()=> {
  const {coaches,allCoaches,createCoach,updateCoach,archiveCoach,restoreCoach,showArchived,toggleShowArchived}=useAuth();
  const [showAddForm,setShowAddForm]=useState(false);
  const [editingCoach,setEditingCoach]=useState(null);
  const [formData,setFormData]=useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role: 'BlackBox'
  });
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [success,setSuccess]=useState('');
  const [archiveModalOpen,setArchiveModalOpen]=useState(false);
  const [restoreModalOpen,setRestoreModalOpen]=useState(false);
  const [coachToAction,setCoachToAction]=useState(null);

  const roleOptions=[
    'BlackBox',
    'eFormula',
    'Commission Blueprint',
    'Omega',
    '123 Profit',
    '7 Day Empires',
    'Amazon FBA/FBM Specialist',
    'Amazon White Label Specialist',
    'Mindset/Accountability Coach',
    'SaaS & AI Expert',
    'Affiliate Marketing Strategist',
    'Social Media & Facebook Groups Specialist',
    'Strategy Coach'
  ];

  const handleChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingCoach) {
        // Update existing coach
        await updateCoach({...formData,id: editingCoach.id});
        setSuccess('Coach updated successfully!');
      } else {
        // Create new coach
        await createCoach(formData);
        setSuccess('Coach added successfully!');
      }

      // Reset form and state
      setFormData({
        name: '',
        username: '',
        email: '',
        password: '',
        role: 'BlackBox'
      });
      setShowAddForm(false);
      setEditingCoach(null);
    } catch (err) {
      setError(err.message || 'Failed to save coach. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick=(coach)=> {
    setEditingCoach(coach);
    setFormData({
      name: coach.name || '',
      username: coach.username || '',
      email: coach.email || '',
      password: '',// Don't populate password for security
      role: coach.role || 'BlackBox'
    });
    setShowAddForm(true);
    setError('');
    setSuccess('');
  };

  const handleArchiveClick=(coach)=> {
    setCoachToAction(coach);
    setArchiveModalOpen(true);
  };

  const handleConfirmArchive=async ()=> {
    if (coachToAction) {
      try {
        await archiveCoach(coachToAction.id);
        setSuccess('Coach archived successfully!');
      } catch (err) {
        setError(err.message || 'Failed to archive coach. Please try again.');
      } finally {
        setArchiveModalOpen(false);
        setCoachToAction(null);
      }
    }
  };

  const handleRestoreClick=(coach)=> {
    setCoachToAction(coach);
    setRestoreModalOpen(true);
  };

  const handleConfirmRestore=async ()=> {
    if (coachToAction) {
      try {
        await restoreCoach(coachToAction.id);
        setSuccess('Coach restored successfully!');
      } catch (err) {
        setError(err.message || 'Failed to restore coach. Please try again.');
      } finally {
        setRestoreModalOpen(false);
        setCoachToAction(null);
      }
    }
  };

  const cancelForm=()=> {
    setShowAddForm(false);
    setEditingCoach(null);
    setFormData({
      name: '',
      username: '',
      email: '',
      password: '',
      role: 'BlackBox'
    });
    setError('');
  };

  return (
    <div className="p-6">
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <Link
            to="/"
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </Link>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage BPA Coaches</h1>
            <p className="text-gray-600">Add,edit,and manage coach accounts for the platform</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleShowArchived}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={showArchived ? FiEyeOff : FiEye} className="w-4 h-4" />
              <span>{showArchived ? "Hide Archived" : "Show Archived"}</span>
            </button>
            <button
              onClick={()=> setShowAddForm(!showAddForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={showAddForm ? FiX : FiPlus} className="w-4 h-4" />
              <span>{showAddForm ? 'Cancel' : 'Add New Coach'}</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{opacity: 0,y: -10}}
          animate={{opacity: 1,y: 0}}
          className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
            {success}
          </div>
          <button onClick={()=> setSuccess('')} className="text-green-700">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </motion.div>
      )}

      {/* Add/Edit Coach Form */}
      {showAddForm && (
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={editingCoach ? FiEdit : FiUserPlus} className="w-5 h-5 mr-2" />
            {editingCoach ? `Edit Coach: ${editingCoach.name}` : 'Add New Coach'}
          </h2>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter coach's full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter username for login"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="w-4 h-4 inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter coach's email address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiLock} className="w-4 h-4 inline mr-2" />
                  {editingCoach ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={editingCoach ? "Enter new password (optional)" : "Create a password"}
                  required={!editingCoach}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specialization / Role
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  {roleOptions.map(role=> (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelForm}
                className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{editingCoach ? 'Update Coach' : 'Create Coach Account'}</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Coaches List - Mobile Optimized */}
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        transition={{delay: 0.2}}
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        {/* Desktop Table - Hidden on mobile */}
        <div className="hidden md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coach
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coaches.map((coach)=> (
                <tr key={coach.id} className={`hover:bg-gray-50 ${coach.is_archived ? 'bg-gray-100' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {coach.name}
                          {coach.is_archived && (
                            <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                              Archived
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {coach.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {coach.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {coach.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {!coach.is_archived && (
                        <>
                          <button
                            onClick={()=> handleEditClick(coach)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={()=> handleArchiveClick(coach)}
                            className="text-amber-600 hover:text-amber-900"
                            title="Archive coach"
                          >
                            <SafeIcon icon={FiArchive} className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {coach.is_archived && (
                        <button
                          onClick={()=> handleRestoreClick(coach)}
                          className="text-green-600 hover:text-green-900"
                          title="Restore coach"
                        >
                          <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards - Visible only on mobile */}
        <div className="md:hidden divide-y divide-gray-200">
          {coaches.map((coach)=> (
            <div key={coach.id} className={`p-4 ${coach.is_archived ? 'bg-gray-100' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900 flex items-center">
                      {coach.name}
                      {coach.is_archived && (
                        <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                          Archived
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {!coach.is_archived && (
                    <>
                      <button
                        onClick={()=> handleEditClick(coach)}
                        className="p-1 text-blue-600 hover:text-blue-900"
                      >
                        <SafeIcon icon={FiEdit} className="w-5 h-5" />
                      </button>
                      <button
                        onClick={()=> handleArchiveClick(coach)}
                        className="p-1 text-amber-600 hover:text-amber-900"
                        title="Archive coach"
                      >
                        <SafeIcon icon={FiArchive} className="w-5 h-5" />
                      </button>
                    </>
                  )}
                  {coach.is_archived && (
                    <button
                      onClick={()=> handleRestoreClick(coach)}
                      className="p-1 text-green-600 hover:text-green-900"
                      title="Restore coach"
                    >
                      <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 space-y-1 text-sm text-gray-500">
                <div className="flex justify-between">
                  <span className="font-medium">Username:</span>
                  <span>{coach.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Email:</span>
                  <span className="text-right">{coach.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Role:</span>
                  <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {coach.role}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {coaches.length===0 && (
          <div className="py-8 text-center">
            <p className="text-gray-500">No coaches found</p>
          </div>
        )}
      </motion.div>

      {/* Archive Confirmation Modal */}
      <AnimatePresence>
        {archiveModalOpen && coachToAction && (
          <DeleteConfirmModal
            title="Archive Coach"
            message={`Are you sure you want to archive ${coachToAction.name}? This will prevent them from logging in,but all their data and history will be preserved.`}
            confirmText="Archive"
            confirmIcon={FiArchive}
            confirmColor="bg-amber-600 hover:bg-amber-700"
            onConfirm={handleConfirmArchive}
            onCancel={()=> {
              setArchiveModalOpen(false);
              setCoachToAction(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Restore Confirmation Modal */}
      <AnimatePresence>
        {restoreModalOpen && coachToAction && (
          <DeleteConfirmModal
            title="Restore Coach"
            message={`Are you sure you want to restore ${coachToAction.name}? This will allow them to log in again.`}
            confirmText="Restore"
            confirmIcon={FiRefreshCw}
            confirmColor="bg-green-600 hover:bg-green-700"
            onConfirm={handleConfirmRestore}
            onCancel={()=> {
              setRestoreModalOpen(false);
              setCoachToAction(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManageCoaches;